type Seen = WeakMap<object, unknown>;

const bothNaN = (a: unknown, b: unknown): boolean =>
    typeof a === 'number' && typeof b === 'number' && Number.isNaN(a) && Number.isNaN(b);

function arraysEqual(a: unknown[], b: unknown[], seen: Seen): boolean {
    return a.length === b.length && a.every((item, index) => equal(item, b[index], seen));
}

function recordsEqual(a: Record<string, unknown>, b: Record<string, unknown>, seen: Seen): boolean {
    const aKeys = Object.keys(a);
    if (aKeys.length !== Object.keys(b).length) return false;
    return aKeys.every(key => Object.prototype.hasOwnProperty.call(b, key) && equal(a[key], b[key], seen));
}

function objectsEqual(a: object, b: object, seen: Seen): boolean {
    if (a instanceof Date || b instanceof Date) {
        return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
    }

    if (a instanceof RegExp || b instanceof RegExp) {
        return a instanceof RegExp && b instanceof RegExp && a.source === b.source && a.flags === b.flags;
    }

    const aIsArray = Array.isArray(a);
    if (aIsArray !== Array.isArray(b)) return false;

    // Guard against circular references (lodash `isEqual` tolerates cycles).
    if (seen.get(a) === b) return true;
    seen.set(a, b);

    return aIsArray
        ? arraysEqual(a as unknown[], b as unknown[], seen)
        : recordsEqual(a as Record<string, unknown>, b as Record<string, unknown>, seen);
}

function equal(a: unknown, b: unknown, seen: Seen): boolean {
    // Identical reference, or strictly-equal primitives (also covers +0 === -0).
    if (a === b) return true;

    if (bothNaN(a, b)) return true;

    // If either side is not a non-null object, they cannot be deeply equal here
    // (strict equality above already handled equal primitives).
    if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
        return false;
    }

    return objectsEqual(a, b, seen);
}

/**
 * Native replacement for lodash/fp `isEqual`. Performs a deep structural
 * equality comparison of two values.
 *
 * Supports the value shapes used across the app: primitives (with SameValueZero
 * semantics, so `NaN` equals `NaN`), plain objects, arrays, `Date`, and
 * `RegExp`. Objects are compared by their own enumerable string keys, and
 * circular references are handled without infinite recursion.
 */
export default function deepEqual(a: unknown, b: unknown): boolean {
    return equal(a, b, new WeakMap());
}
